import * as Phaser from "phaser";
import {mainLogger} from "./client_logger";
import {KITCHEN_SCENE_KEY} from "./scene";
import {getUserDisplayName, getAuthToken} from "./auth";

const logger = mainLogger.child({label: 'client.lobby'});

const LOBBY_BG_COLOR = 0x1a1a2e;
const CARD_BG_COLOR = 0x222244;
const CARD_HOVER_COLOR = 0x333366;
const CARD_BORDER_COLOR = 0x4444AA;
const BUTTON_COLOR = 0x2266AA;
const BUTTON_HOVER_COLOR = 0x3388CC;
const TITLE_COLOR = '#ffffff';
const REFRESH_INTERVAL_MS = 3000;

interface RoomInfo {
    roomId: string;
    roomName: string;
    playerCount: number;
    maxPlayers: number;
    creatorName: string;
}

export class LobbyScene extends Phaser.Scene {
    private roomCards: Phaser.GameObjects.Container[] = [];
    private playerName: string = '';
    private refreshTimer: Phaser.Time.TimerEvent;
    private statusText: Phaser.GameObjects.Text;
    private roomListContainer: Phaser.GameObjects.Container;

    constructor() {
        super({key: 'LobbyScene'});
    }

    create() {
        // Skip lobby when roomId is provided in URL (direct join / E2E tests)
        const params = new URLSearchParams(window.location.search);
        const directRoomId = params.get('roomId');
        if (directRoomId) {
            this.joinRoom(directRoomId);
            return;
        }

        const {width, height} = this.scale;
        this.cameras.main.setBackgroundColor(LOBBY_BG_COLOR);

        // Title
        this.add.text(width / 2, 30, 'Hotplate', {
            fontSize: '28px', color: TITLE_COLOR, fontStyle: 'bold'
        }).setOrigin(0.5, 0);

        // Player name from Clerk identity (falls back to a generated name if not available)
        this.playerName = getUserDisplayName() || `Chef_${Math.floor(Math.random() * 1000)}`;
        const nameLabel = this.add.text(width / 2, 75, `Playing as: ${this.playerName}`, {
            fontSize: '14px', color: '#aaaacc'
        }).setOrigin(0.5, 0);

        // Create Room button
        this.createButton(width / 2, 110, 'Create New Kitchen', () => this.createRoom());

        // Rooms list header
        this.add.text(width / 2, 155, 'Available Kitchens', {
            fontSize: '18px', color: '#ccccff', fontStyle: 'bold'
        }).setOrigin(0.5, 0);

        // Status text
        this.statusText = this.add.text(width / 2, 180, 'Loading rooms...', {
            fontSize: '12px', color: '#888888'
        }).setOrigin(0.5, 0);

        // Room list container
        this.roomListContainer = this.add.container(0, 200);

        // Auto-refresh room list
        this.refreshRooms();
        this.refreshTimer = this.time.addEvent({
            delay: REFRESH_INTERVAL_MS,
            callback: () => this.refreshRooms(),
            loop: true
        });
    }

    private createButton(x: number, y: number, label: string, onClick: () => void): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);
        const textObj = this.add.text(0, 0, label, {
            fontSize: '14px', color: '#ffffff', fontStyle: 'bold'
        }).setOrigin(0.5, 0.5);

        const padding = 12;
        const bg = this.add.graphics();
        const btnWidth = textObj.width + padding * 2;
        const btnHeight = textObj.height + padding;
        bg.fillStyle(BUTTON_COLOR, 1);
        bg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 4);

        container.add([bg, textObj]);
        container.setSize(btnWidth, btnHeight);
        container.setInteractive({useHandCursor: true});

        container.on('pointerover', () => {
            bg.clear();
            bg.fillStyle(BUTTON_HOVER_COLOR, 1);
            bg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 4);
        });
        container.on('pointerout', () => {
            bg.clear();
            bg.fillStyle(BUTTON_COLOR, 1);
            bg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 4);
        });
        container.on('pointerdown', onClick);

        return container;
    }

    private async refreshRooms(): Promise<void> {
        try {
            const baseUrl = window.location.origin;
            const token = await getAuthToken();
            const headers: Record<string, string> = {'Content-Type': 'application/json'};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            const response = await fetch(`${baseUrl}/hotplate/api/rooms`, {headers});
            const rooms: RoomInfo[] = await response.json();
            this.renderRoomList(rooms);
        } catch (error) {
            logger.error(`Failed to fetch rooms: ${error}`);
            this.statusText.setText('Failed to connect to server');
        }
    }

    private renderRoomList(rooms: RoomInfo[]): void {
        // Clear existing cards
        for (const card of this.roomCards) {
            card.destroy();
        }
        this.roomCards = [];

        if (rooms.length === 0) {
            this.statusText.setText('No kitchens available - create one!');
            return;
        }

        this.statusText.setText(`${rooms.length} kitchen${rooms.length > 1 ? 's' : ''} available`);

        const {width} = this.scale;
        const cardWidth = width - 40;
        const cardHeight = 50;
        const cardSpacing = 8;

        rooms.forEach((room, index) => {
            const y = index * (cardHeight + cardSpacing);
            const card = this.createRoomCard(20, y, cardWidth, cardHeight, room);
            this.roomListContainer.add(card);
            this.roomCards.push(card);
        });
    }

    private createRoomCard(x: number, y: number, width: number, height: number, room: RoomInfo): Phaser.GameObjects.Container {
        const card = this.add.container(x, y);

        const bg = this.add.graphics();
        bg.fillStyle(CARD_BG_COLOR, 0.9);
        bg.fillRoundedRect(0, 0, width, height, 4);
        bg.lineStyle(1, CARD_BORDER_COLOR, 0.8);
        bg.strokeRoundedRect(0, 0, width, height, 4);
        card.add(bg);

        // Room name
        const nameText = this.add.text(12, 8, room.roomName, {
            fontSize: '14px', color: '#ffffff', fontStyle: 'bold'
        });
        card.add(nameText);

        // Creator
        const creatorText = this.add.text(12, 28, `by ${room.creatorName}`, {
            fontSize: '10px', color: '#888899'
        });
        card.add(creatorText);

        // Player count
        const countText = this.add.text(width - 80, 8, `${room.playerCount}/${room.maxPlayers}`, {
            fontSize: '14px', color: '#aaccff'
        });
        card.add(countText);

        // Player icons (simple dots)
        const dotsContainer = this.add.container(width - 80, 30);
        for (let i = 0; i < room.maxPlayers; i++) {
            const dot = this.add.graphics();
            const filled = i < room.playerCount;
            dot.fillStyle(filled ? 0x33CC33 : 0x444444, 1);
            dot.fillCircle(i * 10, 0, 3);
            dotsContainer.add(dot);
        }
        card.add(dotsContainer);

        // Join button
        const joinBtn = this.add.text(width - 12, height / 2, 'JOIN', {
            fontSize: '12px', color: '#33cc33', fontStyle: 'bold'
        }).setOrigin(1, 0.5);
        card.add(joinBtn);

        // Make card interactive with explicit hit area matching graphics drawn from (0,0) to (width, height).
        // setSize() centers the hit area at origin, causing the right half (including JOIN text) to miss clicks.
        card.setInteractive(new Phaser.Geom.Rectangle(0, 0, width, height), Phaser.Geom.Rectangle.Contains);
        card.input!.cursor = 'pointer';
        card.on('pointerover', () => {
            bg.clear();
            bg.fillStyle(CARD_HOVER_COLOR, 0.9);
            bg.fillRoundedRect(0, 0, width, height, 4);
            bg.lineStyle(1, CARD_BORDER_COLOR, 1);
            bg.strokeRoundedRect(0, 0, width, height, 4);
        });
        card.on('pointerout', () => {
            bg.clear();
            bg.fillStyle(CARD_BG_COLOR, 0.9);
            bg.fillRoundedRect(0, 0, width, height, 4);
            bg.lineStyle(1, CARD_BORDER_COLOR, 0.8);
            bg.strokeRoundedRect(0, 0, width, height, 4);
        });
        card.on('pointerdown', () => this.joinRoom(room.roomId));

        return card;
    }

    private async createRoom(): Promise<void> {
        try {
            const baseUrl = window.location.origin;
            const token = await getAuthToken();
            const headers: Record<string, string> = {'Content-Type': 'application/json'};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            const response = await fetch(`${baseUrl}/hotplate/api/rooms`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    roomName: `${this.playerName}'s Kitchen`,
                    playerName: this.playerName,
                }),
            });
            const {roomId} = await response.json();
            logger.info(`Created room ${roomId}`);
            this.joinRoom(roomId);
        } catch (error) {
            logger.error(`Failed to create room: ${error}`);
            this.statusText.setText('Failed to create room');
        }
    }

    private joinRoom(roomId: string): void {
        logger.info(`Joining room ${roomId}`);
        if (this.refreshTimer) {
            this.refreshTimer.destroy();
        }
        // Transition to KitchenScene with the roomId
        this.scene.start(KITCHEN_SCENE_KEY, {roomId, playerName: this.playerName});
    }
}
